/* eslint-disable */
import React, {useEffect, useState} from "react";
import Page from "../../../models/api/Page";
import CalendarEventApi, {Action, CalendarEvent, SearchQuery} from "../../../api/calendar/event/CalendarEventApi";
import {showNotification} from "@mantine/notifications";
import {IconBreadOff, IconCheck, IconEdit, IconEye, IconTrash, IconX} from "@tabler/icons-react";
import PageContent from "../../../components/layout/PageContent";
import PageHeader from "../../../components/layout/PageHeader";
import {DataTable, DataTableColumn, DataTableSortStatus, useDataTableColumns} from "mantine-datatable";
import DateView from "../../../components/date/DateView";
import Cell from "../../../components/table/Cell";
import {useDisclosure} from "@mantine/hooks";
import CreateCalendarEventDialog from "../../../components/calendar/event/create/CreateCalendarEventDialog";
import CalendarEventMenu from "./CalendarEventMenu";
import {modals} from "@mantine/modals";
import PaginationFooter from "../../../components/pagination/PaginationFooter";
import CreateFailureReportDialog from "../../failure/report/CreateFailureReportDialog";
import {useContextMenu} from "mantine-contextmenu";
import UpdateCalendarEventDialog from "../../../components/calendar/event/create/UpdateCalendarEventDialog";
import {CalendarEventFilters} from "./CalendarEventFilters";
import {useNavigate} from "react-router-dom";
import FailureReportApi from "../../../api/failure/report/FailureReportApi";
import FailureReport from "../../../api/failure/report/FailureReport";
import UpdateFailureReportDialog from "../../failure/report/UpdateFailureReportDialog";
import {Link} from "@mantine/tiptap";
import {Anchor, Button} from "@mantine/core";

const init: SearchQuery = {
    page: 0,
    limit: 100,
    sortBy: 'date',
    sortDir: 'desc'
}

const initSort: DataTableSortStatus<CalendarEvent> = {
    direction: 'desc',
    columnAccessor: 'date'
};

const tableKey = 'CalendarEvent-tableKey';

const pickDeviceProperty = (record: any, property: string) => {
    return (record.devices.length > 0) ? record.devices[0][property] : undefined;
}

const columns: DataTableColumn<CalendarEvent>[] = [
    {
        accessor: '_id',
        title: '_id',
        defaultToggle: false,
        toggleable: true
    },

    {
        accessor: 'unit',
        title: '#',
        render: r => <Cell orOptional={r.unit}/>,
        toggleable: true,
        defaultToggle: true,
        sortable: true,
        draggable: true
    },

    {
        accessor: 'device',
        title: 'Nazwa urządzenia',
        render: r => <Cell orOptional={pickDeviceProperty(r as any, 'name')}/>,
        toggleable: true,
        defaultToggle: true,
        sortable: false,
        draggable: true
    },

    {
        accessor: 'device_location',
        title: 'Lokalizacja urządzenia',
        render: r => <Cell orOptional={pickDeviceProperty(r as any, 'location')}/>,
        toggleable: true,
        defaultToggle: true,
        sortable: false,
        draggable: true
    },

    {
        accessor: 'device_client_visibleName',
        title: 'Klient',
        render: r => <Cell orOptional={pickDeviceProperty(r as any, 'client')?.visibleName}/>,
        toggleable: true,
        defaultToggle: true,
        sortable: false,
        draggable: true
    },

    {
        accessor: 'device_client_email',
        title: 'Klient',
        render: r => <Cell orOptional={pickDeviceProperty(r as any, 'client')?.emailAddress}/>,
        toggleable: true,
        defaultToggle: true,
        sortable: false,
        draggable: true
    },

    {
        accessor: 'date',
        title: 'Data',
        render: r => <DateView date={(r.date as [number, number, number])}/>,
        toggleable: true,
        defaultToggle: true,
        sortable: true,
        draggable: true
    },

    {
        accessor: 'endDate',
        title: 'Data (Do)',
        render: r => r.endDate ? <DateView date={(r.endDate as [number, number, number])}/> : <>-</>,
        toggleable: true,
        defaultToggle: true,
        sortable: true,
        draggable: true
    },

    {
        accessor: 'comment',
        title: 'Komentarz',
        render: r => <Cell orOptional={r.comment}/>,
        toggleable: true,
        defaultToggle: true,
        sortable: true,
        draggable: true
    },

    {
        accessor: 'failureReport',
        title: "Raport z interwencji",
        render: (f) => f.failureReport ? <Cell orOptional={f.failureReport.title}/> : <>-</>,
        toggleable: true,
        defaultToggle: true,
        sortable: true,
        draggable: true
    },

    {
        accessor: 'compositeReport',
        title: "Raport miesięczny",
        render: (f) => f.failureReport?.compositeReportTitle ? <Anchor href={`/composite/report/excel-details/${f.failureReport.compositeReportID}`}>{f.failureReport?.compositeReportTitle}</Anchor> : <>-</>,
        toggleable: true,
        defaultToggle: true,
        sortable: true,
        draggable: true
    }
]

function CalendarEventPage() {
    const [searchQuery, setSearchQuery] = useState<SearchQuery>(init);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState<Page<CalendarEvent>>();
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<CalendarEvent>>(initSort);
    const [selectedRecords, setSelectedRecords] = useState<CalendarEvent[]>([]);
    const { effectiveColumns } = useDataTableColumns({key: tableKey, columns})
    const [isCreateDialogOpen, createDialog] = useDisclosure();
    const [isFailureDialogOpen, failureDialog] = useDisclosure();
    const [editEvent, setEditEvent] = useState<CalendarEvent>();
    const [editFR, setEditFR] = useState<FailureReport>();
    const nav = useNavigate();

    const { showContextMenu } = useContextMenu();

    const [startLoading, stopLoading] = [
        () => setLoading(true),
        () => setLoading(false)
    ];

    const reloadError = () => {
        showNotification({
            title: 'Nie udało się pobrać danych',
            message: 'Jeśli problem nie ustąpi, proszę skontaktować się z administratorem',
            color: 'red',
            icon: <IconX/>
        });
    }

    const reload = () => {
        startLoading();

        const sorted: SearchQuery = {
            ...searchQuery,
            sortBy: sortStatus.columnAccessor,
            sortDir: sortStatus.direction
        }

        CalendarEventApi.search(sorted)
            .then(setPage)
            .catch(reloadError)
            .finally(stopLoading);
    }

    const createNew = () => {
        createDialog.open();
    }

    const doDeleteSelectedError = () => {
        showNotification({
            title: 'Nie udało się usunąć zaznaczonych elementów',
            message: 'Jeśli problem nie ustąpi, proszę skontaktować się z administratorem',
            color: 'red',
            icon: <IconX/>
        });
    }

    const doDeleteSelectedSuccess = () => {
        showNotification({
            title: 'Usunięto',
            message: 'Pomyślnie usunięto zaznaczone wydarzenia.',
            color: 'green',
            icon: <IconCheck/>
        })
    }

    const clearSelected = () => {
        setSelectedRecords([]);
    }

    const doDeleteSelected = () => {
        const action: Action = {
            action: 'delete',
            selectedElements: selectedRecords.map(record => record.id)
        }

        startLoading();

        CalendarEventApi.performAction(action)
            .then(doDeleteSelectedSuccess)
            .then(clearSelected)
            .then(reload)
            .catch(doDeleteSelectedError);
    }

    const deleteSelected = () => {
        modals.openConfirmModal({
            title: "Usuwanie zaznaczonych",
            children: `Czy na pewno chcesz usunąć ${selectedRecords.length} wydarzeń?`,
            labels: { confirm: `Usuń ${selectedRecords.length} wydarzeń`, cancel: 'Anuluj' },
            confirmProps: {color: 'red'},
            centered: true,
            onConfirm: doDeleteSelected
        });
    }

    const onDelete = (record: CalendarEvent) => {
        CalendarEventApi.performAction({ action: 'delete', selectedElements: [record.id] })
            .then(() => {
                showNotification({
                    title: 'Usunięto wydarzenia z kalendarza!',
                    message: 'Usunięto wydarzenie z kalendarza',
                    color: 'green',
                });
                reload();
            })
            .catch(() => {
                showNotification({
                    title: 'Nie udało się usunąć tego wydarzenia',
                    message: 'Nie udało się usunąć tego wydarzenia',
                    color: 'red',
                });
            });
    }

    const createFR = () => {
        failureDialog.open();
    }

    const filterResults = () => {
        throw new Error('FilterResults is not implemented.');
    }

    const setLimit = (l: number) => setSearchQuery({...searchQuery, limit: l});

    const setPageNumber = (pn: number) => setSearchQuery({...searchQuery, page: pn});

    const missingFailureReport = () => {
        modals.openConfirmModal({
            title: "Raport nie został utworzony.",
            children: "Aby utworzyć raport miesięczny z tego wydarzenia, proszę najpierw zakończyć interwencję.",
            confirmProps: { color: "red" },
            labels: {
                confirm: "OK",
                cancel: "Anuluj"
            }
        });
    }

    const editFailureReport = (r: CalendarEvent) => {
        if (!r.failureReport) {
            missingFailureReport();
            return;
        }
        setEditFR(r.failureReport);
    }

    const viewCompositeReport = (r: CalendarEvent) => {
        if (!r.failureReport?.compositeReportID) {
            missingFailureReport();
            return;
        }
        nav(`/composite/report/excel-details/${r.failureReport.compositeReportID}`);
    }

    const deleteFailureReport = (r: CalendarEvent) => {
        if (!r.failureReport) {
            missingFailureReport();
            return;
        }
        modals.openConfirmModal({
            title: "Czy na pewno chcesz usunąć raport z interwencji",
            children: "Tej operacji nie można cofnąć",
            confirmProps: { color: "red" },
            labels: { confirm: "OK", cancel: "Anuluj" },
            onConfirm: () => {
                FailureReportApi.deleteAll([r.failureReport.id])
                    .then(() => {
                        showNotification({ message: "Raport z interwencji został usunięty", color: "green" })
                    })
                    .then(() => {
                        reload();
                    })
            }
        });
    }

    useEffect(reload, [searchQuery, sortStatus]);

    return (
        <>
            <UpdateFailureReportDialog
                failureReport={editFR}
                onClose={() => { setEditFR(undefined); }}
                closeAndUpdate={() => { setEditFR(undefined); reload(); }}
            />

            <PageHeader
                title="Kalendarz"
                url="/calendar/event"
                action={
                    <CalendarEventMenu
                        filterResults={filterResults}
                        createFailureReport={createFR}
                        createEvent={createNew}
                        deleteSelected={deleteSelected}
                    />
                }
            />

            <PageContent>
                <CalendarEventFilters
                    query={searchQuery}
                    setQuery={setSearchQuery}
                />

                <div style={{marginTop: '30px'}}/>

                <DataTable
                    height="60vh"
                    withColumnBorders
                    styles={{ header: { background: 'white' } }}
                    storeColumnsKey={tableKey}
                    minHeight="150px"
                    fetching={loading}
                    highlightOnHover
                    columns={effectiveColumns}
                    style={{marginBottom: '50px'}}
                    backgroundColor={'transparent'}
                    noRecordsText={"Pusto..."}
                    loaderBackgroundBlur={1}
                    records={page?.content ?? []}
                    selectedRecords={selectedRecords}
                    onSelectedRecordsChange={setSelectedRecords}
                    onRowContextMenu={({record, event}) => {
                        showContextMenu([
                            {
                                key: 'edit',
                                title: `Edycja wydarzenia`,
                                icon: <IconEdit size={16}/>,
                                onClick: () => { setEditEvent(record) },
                            },

                            {
                                key: "frs",
                                title: "Interwencja",
                                icon: <IconBreadOff size={16} />,
                                items: [
                                    {
                                        key: "see-fr",
                                        title: "Wyświetl raport z interwencji",
                                        icon: <IconEye size={16}/>,
                                        onClick: () => { editFailureReport(record); }
                                    },

                                    {
                                        key: "see-fr",
                                        title: "Wyświetl pełny raport",
                                        icon: <IconEye size={16}/>,
                                        onClick: () => viewCompositeReport(record),
                                    },

                                    {
                                        key: "edit-fr",
                                        title: "Edytuj raport z interwencji",
                                        icon: <IconEdit size={16}/>,
                                        onClick: () => { editFailureReport(record); }
                                    },

                                    {
                                        key: "delete-fr",
                                        title: "Usuń raport z interwencji",
                                        color: "red",
                                        icon: <IconTrash size={16}/>,
                                        onClick: () => { deleteFailureReport(record); }
                                    },
                                ]
                            },

                            {
                                key: 'delete',
                                title: 'Usuń',
                                color: 'red',
                                icon: <IconTrash size={16}/>,
                                onClick: () => { onDelete(record); }
                            },
                        ])(event)
                    }}
                    sortStatus={sortStatus}
                    onSortStatusChange={setSortStatus}
                />

                <PaginationFooter
                    setLimit={setLimit}
                    setPageNumber={setPageNumber}
                    pageNumber={searchQuery.page}
                    limit={searchQuery.limit}
                    page={page}
                />
            </PageContent>

            <CreateCalendarEventDialog
                isOpen={isCreateDialogOpen}
                justClose={createDialog.close}
                closeAndUpdate={() => { reload(); createDialog.close(); }}
            />

            <CreateFailureReportDialog
                isOpen={isFailureDialogOpen}
                onClose={() => { failureDialog.close(); }}
                closeAndUpdate={() => {
                    failureDialog.close();
                }}
            />

            <UpdateCalendarEventDialog
                event={editEvent}
                justClose={() => { setEditEvent(undefined); }}
                closeAndUpdate={() => { reload(); setEditEvent(undefined); }}
            />
        </>
    );
}

export default CalendarEventPage;

// TODO: CalendarEventPage
//  - filter results functionality is missing (throws not implemented).
//  - rows will not be highlighted (I am using Cell orOptional)